import React from 'react';
import '../styles/LoadingAnimation.css';

interface LoadingAnimationProps {
  size?: number;
  message?: string;
}

const LoadingAnimationComponent: React.FC<LoadingAnimationProps> = ({
  size = 120,
  message = 'Loading...'
}) => {
  return (
    <div className="loading-animation-container">
      <div className="loading-animation" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          {/* LEGO blue background - right half only */}
          <rect x="100" y="0" width="100" height="200" fill="#0033A0"/>

          {/* 4x4 LEGO baseplate background - right half only (background layer) */}
          <g className="baseplate-background-right">
            {/* 4x4 grid of light grey circles (baseplate studs) - right half only */}
            {[0, 1, 2, 3].map(row =>
              [2, 3].map(col => (
                <circle
                  key={`${row}-${col}`}
                  cx={25 + col * 50}
                  cy={25 + row * 50}
                  r="15.17"
                  fill="none"
                  stroke="#D3D3D3"
                  strokeWidth="2"
                  className="baseplate-stud"
                  style={{
                    animationDelay: `${(row * 4 + col) * 0.05}s`
                  }}
                />
              ))
            )}
          </g>

          {/* Yellow circle segments - animates in last and rotates continuously - BEHIND LEFT SIDE */}
          <g className="yellow-d-shape animate-build" style={{ animationDelay: '1.1s' }}>
            <g className="yellow-segments-rotate">
              {/* Right top quarter (original top half) */}
              <path
                d="M 101.5 1.5 L 101.5 100 L 198.5 100 C 198.5 47.272 153.728 1.5 101.5 1.5 Z M 101.5 51.5 L 101.5 100 L 148.5 100 C 148.5 73.886 126.114 51.5 101.5 51.5 Z"
                fill="#FFD700"
                fillRule="evenodd"
              />
              {/* Right bottom quarter (original bottom half) */}
              <path
                d="M 101.5 100 L 101.5 198.5 C 153.728 198.5 198.5 153.728 198.5 100 L 101.5 100 Z M 101.5 100 L 101.5 148.5 C 126.114 148.5 148.5 126.114 148.5 100 L 101.5 100 Z"
                fill="#FFD700"
                fillRule="evenodd"
              />

              {/* Left top quarter (NEW) */}
              <path
                d="M 101.5 1.5 L 101.5 100 L 4.5 100 C 4.5 47.272 49.272 1.5 101.5 1.5 Z M 101.5 51.5 L 101.5 100 L 54.5 100 C 54.5 73.886 76.886 51.5 101.5 51.5 Z"
                fill="#FFD700"
                fillRule="evenodd"
              />
              {/* Left bottom quarter (NEW) */}
              <path
                d="M 101.5 100 L 101.5 198.5 C 49.272 198.5 4.5 153.728 4.5 100 L 101.5 100 Z M 101.5 100 L 101.5 148.5 C 76.886 148.5 54.5 126.114 54.5 100 L 101.5 100 Z"
                fill="#FFD700"
                fillRule="evenodd"
              />

              {/* Outlines - Right side */}
              <path d="M 101.5 1.5 C 153.728 1.5 198.5 47.272 198.5 100" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 198.5 100 C 198.5 153.728 153.728 198.5 101.5 198.5" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 101.5 51.5 C 126.114 51.5 148.5 73.886 148.5 100" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 148.5 100 C 148.5 126.114 126.114 148.5 101.5 148.5" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 148.5 100 L 198.5 100" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 101.5 1.5 L 101.5 51.5" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 101.5 148.5 L 101.5 198.5" fill="none" stroke="#CC9900" strokeWidth="3"/>

              {/* Outlines - Left side (NEW) */}
              <path d="M 101.5 1.5 C 49.272 1.5 4.5 47.272 4.5 100" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 4.5 100 C 4.5 153.728 49.272 198.5 101.5 198.5" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 101.5 51.5 C 76.886 51.5 54.5 73.886 54.5 100" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 54.5 100 C 54.5 126.114 76.886 148.5 101.5 148.5" fill="none" stroke="#CC9900" strokeWidth="3"/>
              <path d="M 54.5 100 L 4.5 100" fill="none" stroke="#CC9900" strokeWidth="3"/>
            </g>
          </g>

          {/* LEGO blue background - left half ON TOP (covers rotating yellow on left side) */}
          <rect x="0" y="0" width="105" height="200" fill="#0033A0"/>

          {/* 4x4 LEGO baseplate background - left half (top layer) */}
          <g className="baseplate-background-left">
            {/* 4x4 grid of light grey circles (baseplate studs) - left half */}
            {[0, 1, 2, 3].map(row =>
              [0, 1].map(col => (
                <circle
                  key={`left-${row}-${col}`}
                  cx={25 + col * 50}
                  cy={25 + row * 50}
                  r="15.17"
                  fill="none"
                  stroke="#D3D3D3"
                  strokeWidth="2"
                  className="baseplate-stud"
                  style={{
                    animationDelay: `${(row * 4 + col) * 0.05}s`
                  }}
                />
              ))
            )}
          </g>

          {/* Red pieces - VERY TOP LAYER (above everything including blue background and studs) */}

          {/* Red vertical bar - animates in first - TOPMOST */}
          <g className="red-bar animate-build" style={{ animationDelay: '0.2s' }}>
            <rect x="1.5" y="1.5" width="47" height="197" fill="#E4002B" stroke="#B8001F" strokeWidth="3"/>
            <circle cx="25" cy="25" r="15.17" fill="none" stroke="#B8001F" strokeWidth="2"/>
            <circle cx="25" cy="75" r="15.17" fill="none" stroke="#B8001F" strokeWidth="2"/>
            <circle cx="25" cy="125" r="15.17" fill="none" stroke="#B8001F" strokeWidth="2"/>
            <circle cx="25" cy="175" r="15.17" fill="none" stroke="#B8001F" strokeWidth="2"/>
            <rect x="1.5" y="34.83" width="47" height="30.34" fill="#E4002B" stroke="#B8001F" strokeWidth="3"/>
            <rect x="1.5" y="134.83" width="47" height="30.34" fill="#E4002B" stroke="#B8001F" strokeWidth="3"/>
          </g>

          {/* Top red circle - animates in second - TOPMOST */}
          <g className="red-circle-top animate-build" style={{ animationDelay: '0.5s' }}>
            <path
              d="M 50 1.5 A 48.5 48.5 0 1 1 50 98.5 A 48.5 48.5 0 1 1 50 1.5 Z M 50 35.17 A 15.17 15.17 0 1 0 50 65.17 A 15.17 15.17 0 1 0 50 35.17 Z"
              fill="#E4002B"
              stroke="#B8001F"
              strokeWidth="3"
              fillRule="evenodd"
            />
          </g>

          {/* Bottom red circle - animates in third - TOPMOST */}
          <g className="red-circle-bottom animate-build" style={{ animationDelay: '0.8s' }}>
            <path
              d="M 50 101.5 A 48.5 48.5 0 1 1 50 198.5 A 48.5 48.5 0 1 1 50 101.5 Z M 50 135.17 A 15.17 15.17 0 1 0 50 165.17 A 15.17 15.17 0 1 0 50 135.17 Z"
              fill="#E4002B"
              stroke="#B8001F"
              strokeWidth="3"
              fillRule="evenodd"
            />
          </g>
        </svg>
      </div>
      {message && <div className="loading-message">{message}</div>}
    </div>
  );
};

export const LoadingAnimation = React.memo(LoadingAnimationComponent);
